import React, { useState, useEffect } from 'react';
import { Card, List, Tag, Pagination, Typography, Space, Avatar, Button, Empty, Badge, Tooltip, Row, Col, Statistic } from 'antd';
import { CalendarOutlined, EyeOutlined, LikeOutlined, PlusOutlined, CrownOutlined, UserOutlined, FireOutlined, BookOutlined, EditOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

const { Title, Text } = Typography;

// 获取所有用户的文章（管理员专用）
const getAllUsersPosts = () => {
  const allPosts = [];
  
  // 遍历 localStorage 中的所有键
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    
    // 如果是用户文章
    if (key.startsWith('userPosts_')) {
      const userId = key.replace('userPosts_', '');
      const userPosts = JSON.parse(localStorage.getItem(key) || '[]');
      
      // 获取用户信息
      let userInfo = { username: '未知用户', avatar: '👤' };
      
      // 尝试从 Mock 数据获取用户信息
      const mockUsers = [
        { id: 1, username: 'admin', avatar: '👑', isAdmin: true },
        { id: 2, username: 'user1', avatar: '😊', isAdmin: false },
        { id: 3, username: 'test', avatar: '🌟', isAdmin: false },
      ];
      
      const mockUser = mockUsers.find(u => u.id.toString() === userId);
      if (mockUser) {
        userInfo = mockUser;
      }
      
      // 给每篇文章添加用户信息
      const postsWithUserInfo = userPosts.map(post => ({
        ...post,
        userId: parseInt(userId),
        author: post.author || {
          id: parseInt(userId),
          username: userInfo.username,
          avatar: userInfo.avatar,
          isAdmin: userInfo.isAdmin
        }
      }));
      
      allPosts.push(...postsWithUserInfo);
    }
  }
  
  // 按时间倒序排序
  return allPosts.sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
    const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
    return dateB - dateA;
  });
};

// 获取当前用户的文章
const getCurrentUserPosts = () => {
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const userPosts = JSON.parse(localStorage.getItem(`userPosts_${currentUser.id}`) || '[]');
  return userPosts;
};

// 删除文章（管理员可以删除任何文章，普通用户只能删除自己的）
const deletePost = (postId, postUserId) => {
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  
  // 如果是管理员或者文章是自己的
  if (currentUser.isAdmin || currentUser.id === postUserId) {
    // 从对应的用户文章中删除
    const userPosts = JSON.parse(localStorage.getItem(`userPosts_${postUserId}`) || '[]');
    const filteredPosts = userPosts.filter(p => p.id !== postId);
    localStorage.setItem(`userPosts_${postUserId}`, JSON.stringify(filteredPosts));
    
    // 删除点赞记录
    const userLikes = JSON.parse(localStorage.getItem(`userLikes_${postUserId}`) || '{}');
    delete userLikes[postId];
    localStorage.setItem(`userLikes_${postUserId}`, JSON.stringify(userLikes));
    
    return true;
  }
  
  return false;
};

const Home = () => {
  const [posts, setPosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);
  const [total, setTotal] = useState(0);
  const [currentUser, setCurrentUser] = useState({});
  const [stats, setStats] = useState({
    totalViews: 0,
    totalLikes: 0,
    avgViews: 0,
    avgLikes: 0
  });

  useEffect(() => {
    // 获取当前用户信息
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    setCurrentUser(user);
    
    // 根据用户角色加载文章
    let userPosts = [];
    if (user.isAdmin) {
      // 管理员：获取所有用户的文章
      userPosts = getAllUsersPosts();
    } else {
      // 普通用户：只获取自己的文章
      userPosts = getCurrentUserPosts();
    }
    
    setPosts(userPosts);
    setTotal(userPosts.length);
    
    // 计算统计信息
    let totalViews = 0;
    let totalLikes = 0;
    userPosts.forEach(post => {
      totalViews += post.views || 0;
      totalLikes += post.likes || 0;
    });
    
    setStats({
      totalViews,
      totalLikes,
      avgViews: userPosts.length > 0 ? Math.round(totalViews / userPosts.length) : 0,
      avgLikes: userPosts.length > 0 ? Math.round(totalLikes / userPosts.length) : 0
    });
  }, []);

  const getPaginatedData = () => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return posts.slice(start, end);
  };

  const handleDelete = (post, e) => {
    e.stopPropagation();
    e.preventDefault();
    
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const isAdmin = currentUser.isAdmin;
    const isOwner = currentUser.id === post.userId;
    
    let confirmMessage = '确定要删除这篇日记吗？';
    if (isAdmin && !isOwner) {
      confirmMessage = `确定要删除用户 "${post.author?.username || '未知用户'}" 的日记吗？`;
    }
    
    if (window.confirm(confirmMessage)) {
      const success = deletePost(post.id, post.userId);
      if (success) {
        // 重新加载文章
        let updatedPosts = [];
        if (currentUser.isAdmin) {
          updatedPosts = getAllUsersPosts();
        } else {
          updatedPosts = getCurrentUserPosts();
        }
        
        setPosts(updatedPosts);
        setTotal(updatedPosts.length);
        setCurrentPage(1); // 重置到第一页
      }
    }
  };

  const getPopularPosts = () => {
    return [...posts]
      .sort((a, b) => (b.views || 0) - (a.views || 0))
      .slice(0, 3);
  };

  if (posts.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 20px' }}>
        <Empty
          description={
            <span style={{ fontSize: '16px', color: '#666' }}>
              {currentUser.isAdmin ? '还没有任何日记' : '还没有日记，开始你的第一篇吧！'}
            </span>
          }
        >
          {!currentUser.isAdmin && (
            <Link to="/write">
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                size="large"
                style={{
                  height: '48px',
                  fontSize: '16px',
                  padding: '0 30px',
                  borderRadius: '24px',
                  background: 'linear-gradient(135deg, #ff9ebb, #ff9ed8)',
                  border: 'none'
                }}
              >
                写第一篇日记
              </Button>
            </Link>
          )}
        </Empty>
      </div>
    );
  }

  return (
    <div>
      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card 
            style={{ 
              borderRadius: '16px', 
              textAlign: 'center',
              background: 'white',
              border: '1px solid #ffd1e0',
              boxShadow: '0 4px 12px rgba(255, 158, 187, 0.1)'
            }}
          >
            <Statistic
              title="日记总数"
              value={total}
              prefix={<BookOutlined style={{ color: '#ff9ebb' }} />}
              valueStyle={{ color: '#ff9ebb', fontSize: '28px' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card 
            style={{ 
              borderRadius: '16px', 
              textAlign: 'center',
              background: 'white',
              border: '1px solid #ffd1e0',
              boxShadow: '0 4px 12px rgba(255, 158, 187, 0.1)'
            }}
          >
            <Statistic
              title="总浏览量"
              value={stats.totalViews}
              prefix={<EyeOutlined style={{ color: '#ff9ebb' }} />}
              valueStyle={{ color: '#ff9ebb', fontSize: '28px' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card 
            style={{ 
              borderRadius: '16px', 
              textAlign: 'center',
              background: 'white',
              border: '1px solid #ffd1e0',
              boxShadow: '0 4px 12px rgba(255, 158, 187, 0.1)'
            }}
          >
            <Statistic
              title="总点赞数"
              value={stats.totalLikes}
              prefix={<LikeOutlined style={{ color: '#ff9ebb' }} />}
              valueStyle={{ color: '#ff9ebb', fontSize: '28px' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card 
            style={{ 
              borderRadius: '16px', 
              textAlign: 'center',
              background: 'white',
              border: '1px solid #ffd1e0',
              boxShadow: '0 4px 12px rgba(255, 158, 187, 0.1)'
            }}
          >
            <Statistic
              title="平均阅读"
              value={stats.avgViews}
              prefix={<FireOutlined style={{ color: '#ff9ebb' }} />}
              valueStyle={{ color: '#ff9ebb', fontSize: '28px' }}
            />
          </Card>
        </Col>
      </Row>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, color: '#333' }}>
          {currentUser.isAdmin ? '📚 所有日记' : '📖 我的日记'}
          {currentUser.isAdmin && (
            <Badge 
              count="管理员" 
              style={{ 
                backgroundColor: '#ff9ebb', 
                marginLeft: 10,
                fontSize: '12px',
                fontWeight: 'normal'
              }} 
            />
          )}
        </Title>
        {!currentUser.isAdmin && (
          <Link to="/write">
            <Button 
              type="primary" 
              icon={<EditOutlined />}
              style={{
                borderRadius: '24px',
                height: '44px',
                padding: '0 24px',
                background: 'linear-gradient(135deg, #ff9ebb, #ff9ed8)',
                border: 'none'
              }}
            >
              写日记
            </Button>
          </Link>
        )}
      </div>
      
      {currentUser.isAdmin && (
        <Card style={{ 
          marginBottom: 16, 
          background: 'rgba(255, 158, 187, 0.1)',
          border: '1px solid #ffd1e0',
          borderRadius: '12px'
        }}>
          <Space>
            <CrownOutlined style={{ color: '#ff9ebb', fontSize: '18px' }} />
            <Text style={{ color: '#666' }}>
              管理员模式：您可以查看和删除所有用户的日记
            </Text>
          </Space>
        </Card>
      )}
      
      {/* 热门日记（如果有数据） */}
      {posts.length >= 5 && (
        <Card 
          style={{ 
            marginBottom: 24,
            background: 'white',
            border: '1px solid #ffd1e0',
            borderRadius: '16px'
          }}
          title={
            <Space>
              <FireOutlined style={{ color: '#ff6b6b' }} />
              <Text strong style={{ color: '#333' }}>🔥 热门日记</Text>
            </Space>
          }
        >
          <Row gutter={[16, 16]}>
            {getPopularPosts().map(post => (
              <Col key={post.id} xs={24} md={8}>
                <Card
                  hoverable
                  style={{ 
                    borderRadius: '12px',
                    border: '1px solid #ffd1e0',
                    background: 'white'
                  }}
                  onClick={() => window.location.href = `/post/${post.id}`}
                >
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Text strong ellipsis style={{ fontSize: '14px', color: '#333' }}>
                      {post.title}
                    </Text>
                    <Space size={8}>
                      <Avatar size="small" style={{ background: '#fde3cf' }}>
                        {post.author?.avatar || '👤'}
                      </Avatar>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {post.author?.username}
                      </Text>
                    </Space>
                    <Space split="·" style={{ fontSize: '12px', color: '#666' }}>
                      <span>👁 {post.views || 0}</span>
                      <span>❤️ {post.likes || 0}</span>
                    </Space>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}
      
      <List
        grid={{ gutter: 20, column: 1 }}
        dataSource={getPaginatedData()}
        renderItem={post => (
          <List.Item>
            <Link to={`/post/${post.id}`} style={{ width: '100%', textDecoration: 'none' }}>
              <Card
                hoverable
                style={{ 
                  width: '100%', 
                  borderRadius: '16px',
                  overflow: 'hidden',
                  background: 'white',
                  border: '1px solid #ffd1e0',
                  transition: 'all 0.3s'
                }}
                bodyStyle={{ padding: '24px' }}
                onClick={(e) => {
                  if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
                    e.stopPropagation();
                    e.preventDefault();
                  }
                }}
              >
                <div style={{ position: 'relative' }}>
                  {/* 热度标签 */}
                  {(post.views || 0) > 50 && (
                    <div style={{ 
                      position: 'absolute', 
                      top: -12, 
                      right: -12, 
                      background: 'linear-gradient(135deg, #ff6b6b, #ff9ebb)',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      zIndex: 1,
                      boxShadow: '0 4px 12px rgba(255, 107, 107, 0.3)'
                    }}>
                      🔥 热门
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Title level={4} style={{ margin: 0, flex: 1, color: '#333' }}>
                      {post.title}
                    </Title>
                    <Button 
                      danger 
                      size="small"
                      onClick={(e) => handleDelete(post, e)}
                      style={{ borderRadius: '12px' }}
                    >
                      删除
                    </Button>
                  </div>
                  
                  {post.summary && (
                    <Text type="secondary" style={{ 
                      fontSize: 14, 
                      display: 'block', 
                      marginTop: 8,
                      lineHeight: 1.6,
                      color: '#666'
                    }}>
                      {post.summary}
                    </Text>
                  )}
                  
                  <Space wrap style={{ marginTop: 12 }}>
                    {post.tags && post.tags.map(tag => (
                      <Tag 
                        key={tag} 
                        style={{ 
                          background: 'rgba(255, 158, 187, 0.1)',
                          borderColor: '#ff9ebb',
                          color: '#ff7ba9',
                          borderRadius: '12px',
                          padding: '4px 12px',
                          fontSize: '13px'
                        }}
                      >
                        {tag}
                      </Tag>
                    ))}
                  </Space>
                  
                  <Space 
                    split={<Text type="secondary" style={{ color: '#ddd' }}>·</Text>} 
                    style={{ 
                      fontSize: 13, 
                      marginTop: 20,
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap'
                    }}
                  >
                    <Space size={4}>
                      {currentUser.isAdmin && (
                        <>
                          {post.author?.isAdmin ? (
                            <Tooltip title="管理员">
                              <CrownOutlined style={{ color: '#ff9ebb' }} />
                            </Tooltip>
                          ) : (
                            <UserOutlined style={{ color: '#999' }} />
                          )}
                        </>
                      )}
                      <Avatar size="small" style={{ 
                        backgroundColor: post.author?.isAdmin ? '#fff9db' : '#fde3cf',
                        border: post.author?.isAdmin ? '2px solid gold' : 'none'
                      }}>
                        {post.author?.avatar || '👤'}
                      </Avatar>
                      <Text style={{ color: '#666' }}>{post.author?.username || '未知用户'}</Text>
                      {currentUser.isAdmin && currentUser.id !== post.userId && (
                        <Text type="secondary" style={{ fontSize: 11, color: '#999' }}>
                          (ID: {post.userId})
                        </Text>
                      )}
                    </Space>
                    
                    <Space size={4}>
                      <CalendarOutlined style={{ color: '#999' }} />
                      <Text style={{ color: '#666' }}>{post.createdAt ? new Date(post.createdAt).toLocaleDateString() : '刚刚'}</Text>
                    </Space>
                    
                    <Space size={4}>
                      <EyeOutlined style={{ color: '#999' }} />
                      <Text style={{ color: '#666' }}>{post.views || 0}</Text>
                    </Space>
                    
                    <Space size={4}>
                      <LikeOutlined style={{ color: '#999' }} />
                      <Text style={{ color: '#666' }}>{post.likes || 0}</Text>
                    </Space>
                    
                    {/* 文章时长估计 */}
                    {post.content && (
                      <Space size={4}>
                        <Text type="secondary" style={{ color: '#999' }}>⏱️ {Math.ceil(post.content.length / 500)}分钟阅读</Text>
                      </Space>
                    )}
                  </Space>
                </div>
              </Card>
            </Link>
          </List.Item>
        )}
      />
      
      <div style={{ textAlign: 'center', marginTop: 30 }}>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={total}
          onChange={(page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          }}
          showSizeChanger
          showQuickJumper
          showTotal={(total, range) => (
            <Text style={{ color: '#666' }}>
              {range[0]}-{range[1]} 共 {total} 篇
            </Text>
          )}
          style={{ margin: '0 auto' }}
        />
      </div>
    </div>
  );
};

export default Home;